import type { DeliveryZone, DeliveryZonePoint } from '@choco-admin/types';
import React, { useCallback, useEffect, useRef } from 'react';

import type {
  GoogleMapInstance,
  GoogleMapsListener,
  GooglePolygon,
} from '../../../../../../components/GooglePlaces/hooks/useGooglePlaces';

/** Berlin, so an empty map opens somewhere recognisable rather than mid-ocean. */
const FALLBACK_CENTER = { lat: 52.52, lng: 13.405 };
const FALLBACK_ZOOM = 11;

const SELECTED_COLOR = '#006FEE';
const UNSELECTED_COLOR = '#889096';

interface DeliveryZonesMapProps {
  zones: DeliveryZone[];
  /** Index into `zones`, or null when nothing is being edited. */
  selectedIndex: number | null;
  /** Corners placed so far for a new area; null when not drawing. */
  draftPoints: DeliveryZonePoint[] | null;
  onDraftPointAdd: (point: DeliveryZonePoint) => void;
  onZonePointsChange: (index: number, points: DeliveryZonePoint[]) => void;
  onZoneSelect: (index: number) => void;
}

const readPoints = (polygon: GooglePolygon): DeliveryZonePoint[] => {
  const path = polygon.getPath();
  const points: DeliveryZonePoint[] = [];

  for (let i = 0; i < path.getLength(); i += 1) {
    const point = path.getAt(i);
    points.push({ latitude: point.lat(), longitude: point.lng() });
  }

  return points;
};

const toLatLngLiteral = (point: DeliveryZonePoint) => ({
  lat: point.latitude,
  lng: point.longitude,
});

/**
 * The map surface: saved areas as polygons, plus drawing and reshaping.
 *
 * Drawing is built from map clicks rather than Google's DrawingManager, which
 * was removed from the Maps JavaScript API in version 3.65 and now throws on
 * construction. Each click adds a corner; the parent decides when the shape is
 * finished.
 *
 * Google owns the polygon objects, so they live in refs. React state is the
 * source of truth for which zones exist, not for their live geometry: reshaping
 * reports new points upward on every path change.
 */
export const DeliveryZonesMap: React.FC<DeliveryZonesMapProps> = ({
  zones,
  selectedIndex,
  draftPoints,
  onDraftPointAdd,
  onZonePointsChange,
  onZoneSelect,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<GoogleMapInstance>();
  const polygonsRef = useRef<GooglePolygon[]>([]);
  const listenersRef = useRef<GoogleMapsListener[]>([]);
  const draftShapesRef = useRef<
    { setMap(map: GoogleMapInstance | null): void }[]
  >([]);
  const isDrawing = draftPoints !== null;
  // Which framing the map is currently showing, so a geometry edit does not
  // re-fit. See the redraw effect below.
  const lastFramingKeyRef = useRef<string>('');
  // What the polygons on the map currently represent: how many there are, which
  // one is selected, and each one's corners. Together these decide whether a
  // render needs new polygons or just new paths. See the redraw effect.
  const drawnStructureRef = useRef<string>('');
  const drawnPointsRef = useRef<string[]>([]);

  // Callbacks land in Google listeners that outlive a render, so they are read
  // through refs. A listener registered on mount would otherwise keep calling
  // the first render's handlers and write stale zone indexes.
  const handlersRef = useRef({
    onDraftPointAdd,
    onZonePointsChange,
    onZoneSelect,
  });
  useEffect(() => {
    handlersRef.current = { onDraftPointAdd, onZonePointsChange, onZoneSelect };
  }, [onDraftPointAdd, onZonePointsChange, onZoneSelect]);

  const clearPolygons = useCallback(() => {
    listenersRef.current.forEach((listener) => listener.remove());
    listenersRef.current = [];
    polygonsRef.current.forEach((polygon) => polygon.setMap(null));
    polygonsRef.current = [];
  }, []);

  // Create the map once.
  useEffect(() => {
    const maps = window.google?.maps;
    if (!maps || !containerRef.current || mapRef.current) return;

    mapRef.current = new maps.Map(containerRef.current, {
      center: FALLBACK_CENTER,
      zoom: FALLBACK_ZOOM,
      streetViewControl: false,
      mapTypeControl: false,
    });
  }, []);

  // Saved areas.
  //
  // Polygons are rebuilt when the set of areas or the selection changes, and
  // NOT when only their geometry does. Dragging a corner updates `zones`, and
  // rebuilding there destroyed the polygon the mouse was holding: Google lost
  // the drag target after the first `set_at`, so a corner could be nudged but
  // never moved. Geometry-only renders update paths in place instead, skipping
  // the selected polygon, whose own path is what changed.
  useEffect(() => {
    const maps = window.google?.maps;
    const map = mapRef.current;
    if (!maps || !map) return undefined;

    const pointsKeys = zones.map((zone) =>
      zone.points.map((p) => `${p.latitude},${p.longitude}`).join('|')
    );
    const structureKey = `${zones.length}|${selectedIndex ?? 'none'}|${isDrawing}`;

    if (
      structureKey === drawnStructureRef.current &&
      polygonsRef.current.length === zones.length
    ) {
      pointsKeys.forEach((key, index) => {
        if (key === drawnPointsRef.current[index]) return;
        drawnPointsRef.current[index] = key;
        // The selected polygon is the editable one, so a change to it came from
        // the map itself and is already drawn. Writing the path back mid-drag
        // would fight the gesture.
        if (index === selectedIndex) return;
        polygonsRef.current[index].setPath(
          zones[index].points.map(toLatLngLiteral)
        );
      });
      return undefined;
    }

    drawnStructureRef.current = structureKey;
    drawnPointsRef.current = pointsKeys;

    clearPolygons();

    const bounds = new maps.LatLngBounds();
    const selectedBounds = new maps.LatLngBounds();

    zones.forEach((zone, index) => {
      const isSelected = index === selectedIndex;
      const polygon = new maps.Polygon({
        paths: zone.points.map(toLatLngLiteral),
        map,
        // A polygon under the cursor would swallow the clicks that place new
        // corners, so editing is off while drawing.
        editable: isSelected && !isDrawing,
        clickable: !isDrawing,
        strokeColor: isSelected ? SELECTED_COLOR : UNSELECTED_COLOR,
        strokeWeight: 2,
        fillColor: isSelected ? SELECTED_COLOR : UNSELECTED_COLOR,
        fillOpacity: isSelected ? 0.25 : 0.12,
      });

      polygonsRef.current.push(polygon);
      zone.points.forEach((point) => {
        bounds.extend(toLatLngLiteral(point));
        if (isSelected) selectedBounds.extend(toLatLngLiteral(point));
      });

      listenersRef.current.push(
        polygon.addListener('click', () =>
          handlersRef.current.onZoneSelect(index)
        )
      );

      if (isSelected && !isDrawing) {
        // Vertex edits fire on the path, not the polygon, and each of the three
        // events covers a different gesture: moving, adding, deleting.
        const path = polygon.getPath();
        const report = () =>
          handlersRef.current.onZonePointsChange(index, readPoints(polygon));

        ['set_at', 'insert_at', 'remove_at'].forEach((event) => {
          listenersRef.current.push(path.addListener(event, report));
        });
      }
    });

    // Frame the areas only when the framing should actually change: on first
    // load, when an area is added or removed, or when one is selected from the
    // list. NOT on a geometry edit.
    //
    // This effect re-runs on every vertex move, because reshaping updates
    // `zones`. Refitting there re-zooms and re-centres the map under the cursor
    // on each corner drag, which makes editing a shape almost unusable. Drawing
    // is excluded for the same reason, between clicks.
    // Selecting an area frames THAT area, because the button says "Show <name>
    // on the map". Framing every area instead pulls the view back out to the
    // overview, which throws away the zoom someone was working at.
    const framingKey = `${zones.length}|${selectedIndex ?? 'none'}`;
    const target = selectedBounds.isEmpty() ? bounds : selectedBounds;
    if (
      !target.isEmpty() &&
      !isDrawing &&
      framingKey !== lastFramingKeyRef.current
    ) {
      lastFramingKeyRef.current = framingKey;
      map.fitBounds(target);
    }

    return undefined;
  }, [zones, selectedIndex, isDrawing, clearPolygons]);

  // Teardown belongs on unmount only. As this effect's cleanup it ran before
  // every rebuild, including the geometry-only renders that must leave the
  // polygons alone.
  useEffect(() => clearPolygons, [clearPolygons]);

  // Collect corners while drawing.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isDrawing) return undefined;

    map.setOptions({ draggableCursor: 'crosshair' });

    const listener = map.addListener('click', (event) => {
      const position = event.latLng;
      if (!position) return;
      handlersRef.current.onDraftPointAdd({
        latitude: position.lat(),
        longitude: position.lng(),
      });
    });

    return () => {
      listener.remove();
      map.setOptions({ draggableCursor: undefined });
    };
  }, [isDrawing]);

  // The shape in progress: a line for the first two corners, a filled outline
  // from the third on, so it reads as an area as soon as it is one.
  useEffect(() => {
    const maps = window.google?.maps;
    const map = mapRef.current;
    if (!maps || !map) return undefined;

    draftShapesRef.current.forEach((shape) => shape.setMap(null));
    draftShapesRef.current = [];

    if (!draftPoints || draftPoints.length === 0) return undefined;

    const path = draftPoints.map(toLatLngLiteral);

    draftShapesRef.current.push(
      draftPoints.length < 3
        ? new maps.Polyline({
            path,
            map,
            strokeColor: SELECTED_COLOR,
            strokeWeight: 2,
          })
        : new maps.Polygon({
            paths: path,
            map,
            clickable: false,
            strokeColor: SELECTED_COLOR,
            strokeWeight: 2,
            fillColor: SELECTED_COLOR,
            fillOpacity: 0.2,
          })
    );

    return () => {
      draftShapesRef.current.forEach((shape) => shape.setMap(null));
      draftShapesRef.current = [];
    };
  }, [draftPoints]);

  return (
    <div
      ref={containerRef}
      data-testid="delivery-zones-map"
      className="h-[28rem] w-full overflow-hidden rounded-lg border border-default-200 bg-default-100"
    />
  );
};
